import React, { useContext, useState, useEffect, createContext } from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  deleteUser
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { deleteUserDocument } from '../services/firestoreService';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const signup = async (email, password, name) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    // Create a user document in Firestore to store profile info
    await setDoc(doc(db, 'users', user.uid), {
      name,
      email,
      createdAt: new Date(),
    });
    return userCredential;
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    setCurrentUser(null);
    setCurrentUserData(null);
    return signOut(auth);
  };
  
  const fullDeleteAccount = async () => {
    if (!currentUser) throw new Error("No user is logged in to delete.");
    
    const userToDelete = currentUser;
    
    try {
        // 1. Delete Firestore document.
        await deleteUserDocument(userToDelete.uid);
        
        // 2. Delete the user from Firebase Authentication. This will trigger onAuthStateChanged.
        await deleteUser(userToDelete);

    } catch (error) {
        console.error("Error deleting account:", error);
        // This can fail if the user needs to re-authenticate for security reasons.
        alert(`Error deleting account: ${error.message}. You may need to log out and log back in to complete this action.`);
        throw error;
    }
  };

  const fetchUserData = async (user) => {
      if (!user) {
          setCurrentUserData(null);
          return;
      }
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        setCurrentUserData({ uid: user.uid, ...userDoc.data() });
      }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await fetchUserData(user);
      setLoading(false);
    });

    return unsubscribe; // Cleanup subscription on unmount
  }, []);

  const value = {
    currentUser,
    currentUserData,
    login,
    signup,
    logout,
    fullDeleteAccount,
    refreshUserData: () => fetchUserData(currentUser),
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};